/**
 * Leser et regneark (.xlsx/.csv) i nettleseren og gir CSV-tekst til serveren.
 * Serveren tolker aldri binære regneark; kontrollene her er brukervennlighet, ikke sikkerhet.
 * SheetJS lastes dynamisk først når en fil faktisk leses.
 */
import type { ImportFeilRad } from '@/services/api/types';

export type LesResultat = { csv: string } | { feil: ImportFeilRad[] };

const MAKS_FILSTORRELSE = 5 * 1024 * 1024;
// Standardgrense, lik serverens for talentimporten: overskrift + 2000 datarader. Stopper også regneark som deklarerer enorme områder.
const MAKS_DATARADER = 2000;
const MAKS_FORMELFEIL = 100;

const feil = (melding: string): LesResultat => ({ feil: [{ melding }] });

const UTF8_FEIL =
  'Filen er ikke lagret som UTF-8. I Excel: Lagre som → CSV UTF-8. I Google Sheets: Fil → Last ned → CSV.';

const leseBytes = (fil: File): Promise<ArrayBuffer> => {
  if (typeof fil.arrayBuffer === 'function') return fil.arrayBuffer();
  return new Promise((resolve, reject) => {
    const leser = new FileReader();
    leser.onload = () => resolve(leser.result as ArrayBuffer);
    leser.onerror = () => reject(leser.error);
    leser.readAsArrayBuffer(fil);
  });
};

/** En CSV-linje per regnearkrad; tomme rader beholdes, avsluttende tomme rader kuttes. */
const tilCsv = (rader: string[][]): string => {
  const sitert = (v: string) => (/[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const linjer = rader.map((r) => {
    let slutt = r.length;
    while (slutt > 0 && r[slutt - 1] === '') slutt -= 1;
    return r.slice(0, slutt).map(sitert).join(',');
  });
  while (linjer.length > 0 && linjer[linjer.length - 1] === '') linjer.pop();
  return linjer.join('\n');
};

const lesCsvFil = async (fil: File): Promise<LesResultat> => {
  const buf = await leseBytes(fil);
  let tekst: string;
  try {
    tekst = new TextDecoder('utf-8', { fatal: true }).decode(buf);
  } catch {
    return feil(UTF8_FEIL);
  }
  if (tekst.charCodeAt(0) === 0xfeff) tekst = tekst.slice(1);
  // Serverens lesCsv er eneste CSV-parser (skilletegn, sitater, linjeskift): teksten sendes uendret.
  return { csv: tekst };
};

type SheetJS = typeof import('xlsx');

/** Visningstekst for en celle; datoer blir YYYY-MM-DD (uavhengig av tidssone). */
const celleTekst = (XLSX: SheetJS, celle: import('xlsx').CellObject): string => {
  if (celle.t === 'n' && typeof celle.v === 'number' && celle.z && XLSX.SSF.is_date(String(celle.z))) {
    const d = XLSX.SSF.parse_date_code(celle.v);
    if (d) return `${String(d.y).padStart(4, '0')}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
  }
  if (celle.t === 's') return String(celle.v ?? '');
  if (celle.w !== undefined) return celle.w;
  return celle.v === undefined || celle.v === null ? '' : String(celle.v);
};

const erZip = (buf: ArrayBuffer): boolean => {
  const m = new Uint8Array(buf, 0, Math.min(4, buf.byteLength));
  return m.length === 4 && m[0] === 0x50 && m[1] === 0x4b && m[2] === 0x03 && m[3] === 0x04;
};

interface ArkInnhold {
  celler: Map<number, Map<number, string>>;
  formelFeil: ImportFeilRad[];
  sisteRad: number;
}

/** Samler celletekst per rad/kolonne og formelfeil (formler støttes ikke). */
const samleCeller = (XLSX: SheetJS, ark: import('xlsx').WorkSheet): ArkInnhold => {
  const inn: ArkInnhold = { celler: new Map(), formelFeil: [], sisteRad: -1 };
  for (const adresse of Object.keys(ark)) {
    if (adresse.startsWith('!')) continue;
    const celle = ark[adresse];
    const { r, c } = XLSX.utils.decode_cell(adresse);
    if (celle.f !== undefined) {
      if (inn.formelFeil.length < MAKS_FORMELFEIL) {
        inn.formelFeil.push({ melding: `Rad ${r + 1}: formler støttes ikke – lim inn som verdi` });
      }
      continue;
    }
    const tekst = celleTekst(XLSX, celle);
    if (tekst === '') continue;
    if (!inn.celler.has(r)) inn.celler.set(r, new Map());
    inn.celler.get(r)!.set(c, tekst);
    if (r > inn.sisteRad) inn.sisteRad = r;
  }
  return inn;
};

const tilRader = ({ celler, sisteRad }: ArkInnhold): string[][] => {
  const rader: string[][] = [];
  for (let r = 0; r <= sisteRad; r += 1) {
    const ut: string[] = [];
    celler.get(r)?.forEach((v, c) => { ut[c] = v; });
    rader.push(Array.from(ut, (v) => v ?? ''));
  }
  return rader;
};

const sorterFormelFeil = (feilListe: ImportFeilRad[]): ImportFeilRad[] =>
  [...feilListe].sort((a, b) => Number(/\d+/.exec(a.melding)?.[0]) - Number(/\d+/.exec(b.melding)?.[0]));

const lesXlsxFil = async (fil: File, maksRader: number): Promise<LesResultat> => {
  const buf = await leseBytes(fil);
  if (!erZip(buf)) {
    return feil('Filen er ikke en gyldig .xlsx-fil. Lagre den som .xlsx (Excel-arbeidsbok) eller bruk .csv.');
  }
  const XLSX = await import('xlsx');
  let wb;
  try {
    wb = XLSX.read(buf, { type: 'array', cellFormula: true, cellNF: true, cellText: true, sheetRows: maksRader + 2 });
  } catch {
    return feil('Kunne ikke lese regnearket. Kontroller at filen er en gyldig .xlsx-fil.');
  }
  const arknavn = wb.SheetNames[0];
  const ark = arknavn ? wb.Sheets[arknavn] : undefined;
  if (!ark) return { csv: '' };

  const inn = samleCeller(XLSX, ark);
  if (inn.formelFeil.length > 0) return { feil: sorterFormelFeil(inn.formelFeil) };
  if (inn.sisteRad > maksRader) return feil(`Maks ${maksRader} rader`);
  return { csv: tilCsv(tilRader(inn)) };
};

export const lesRegneark = async (
  fil: File,
  { maksRader = MAKS_DATARADER }: { maksRader?: number } = {}
): Promise<LesResultat> => {
  if (fil.size > MAKS_FILSTORRELSE) return feil('Filen er for stor (maks 5 MB).');
  const navn = fil.name.toLowerCase();
  if (navn.endsWith('.csv')) return lesCsvFil(fil);
  if (navn.endsWith('.xlsx')) return lesXlsxFil(fil, maksRader);
  return feil('Filtypen støttes ikke. Bruk .xlsx eller .csv.');
};
